import { yupResolver } from "@hookform/resolvers/yup";
import { motion } from "framer-motion";
import { Lock, Mail, Shield } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import * as yup from "yup";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "./AuthContext";
import { logoutJWT } from "./auth-client";
import { TwoFactorSetup } from "./components/TwoFactorSetup";
import { TwoFactorVerify } from "./components/TwoFactorVerify";

const schema = yup.object({
  email: yup.string().email("Email inválido").required("Email é obrigatório"),
  password: yup.string().required("Senha é obrigatória"),
});

type LoginFormData = yup.InferType<typeof schema>;

export function Login() {
  const { isAuthenticated, login, complete2FASetup, complete2FALogin } =
    useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [loginStep, setLoginStep] = useState<
    "login" | "setup2fa" | "verify2fa"
  >("login");
  const [tempToken, setTempToken] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const from = (location.state as any)?.from?.pathname || "/";

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError("");

    try {
      const result = await login(data.email, data.password);

      if (result.requires2FASetup) {
        // User needs to set up 2FA first
        setTempToken(result.tempToken!);
        setLoginStep("setup2fa");
      } else if (result.requiresTwoFactor) {
        // User needs to verify 2FA
        setTempToken(result.tempToken!);
        setLoginStep("verify2fa");
      } else {
        if (result.user && !result.user.opsAssociations.includes("gestor")) {
          setError("Acesso negado. Este dashboard é apenas para gestores.");
          logoutJWT();
          return;
        }
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || "Erro ao fazer login");
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FASetupComplete = (user: any) => {
    if (!user.opsAssociations.includes("gestor")) {
      setError("Acesso negado. Este dashboard é apenas para gestores.");
      logoutJWT();
      setLoginStep("login");
      setTempToken("");
      return;
    }
    complete2FASetup(user);
    navigate(from, { replace: true });
  };

  const handle2FAVerifyComplete = (user: any) => {
    if (!user.opsAssociations.includes("gestor")) {
      setError("Acesso negado. Este dashboard é apenas para gestores.");
      logoutJWT();
      setLoginStep("login");
      setTempToken("");
      return;
    }
    complete2FALogin(user);
    navigate(from, { replace: true });
  };

  const handle2FAError = (error: string) => {
    setError(error);
    // Reset to login step
    setLoginStep("login");
    setTempToken("");
  };

  // Render different steps based on login flow
  if (loginStep === "setup2fa") {
    return (
      <TwoFactorSetup
        tempToken={tempToken}
        onComplete={handle2FASetupComplete}
        onError={handle2FAError}
      />
    );
  }

  if (loginStep === "verify2fa") {
    return (
      <TwoFactorVerify
        tempToken={tempToken}
        onComplete={handle2FAVerifyComplete}
        onError={handle2FAError}
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-[hsl(210,35%,32%)] via-[hsl(210,40%,25%)] to-[hsl(210,50%,10%)]" />

      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-1/2 -left-1/2 w-full h-full rounded-full bg-[hsl(43,56%,61%)]/10 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.div
          className="absolute -bottom-1/2 -right-1/2 w-full h-full rounded-full bg-[hsl(43,56%,61%)]/10 blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <motion.div
          className="mb-8 text-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="inline-flex items-center justify-center w-20 h-20 mb-4 rounded-2xl bg-gradient-to-br from-[hsl(43,56%,61%)] to-[hsl(43,56%,51%)] shadow-lg">
            <img
              src="/favicon.png"
              alt="Planalto Logo"
              className="w-16 h-16 object-contain"
            />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">
            Dashboard Gestor
          </h1>
          <p className="text-white/70 flex items-center justify-center gap-2">
            <Shield className="w-4 h-4" />
            Planalto Capital
          </p>
        </motion.div>

        <Card className="backdrop-blur-xl bg-white/10 border-white/20 shadow-2xl">
          <CardHeader className="text-center space-y-2 pb-6">
            <CardTitle className="text-2xl font-bold text-white">
              Bem-vindo
            </CardTitle>
            <p className="text-white/70">Faça login para acessar o dashboard</p>
          </CardHeader>
          <CardContent className="space-y-4">
            {error && (
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <Alert
                  variant="destructive"
                  className="bg-red-500/20 border-red-500/50 text-white"
                >
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              </motion.div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <motion.div
                className="space-y-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Label htmlFor="email" className="text-white">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    {...register("email")}
                    className={`pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/20 focus:border-[hsl(43,56%,61%)] transition-all ${
                      errors.email ? "border-red-500" : ""
                    }`}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-red-300">{errors.email.message}</p>
                )}
              </motion.div>

              <motion.div
                className="space-y-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Label htmlFor="password" className="text-white">
                  Senha
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/50" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    {...register("password")}
                    className={`pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:bg-white/20 focus:border-[hsl(43,56%,61%)] transition-all ${
                      errors.password ? "border-red-500" : ""
                    }`}
                  />
                </div>
                {errors.password && (
                  <p className="text-sm text-red-300">
                    {errors.password.message}
                  </p>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[hsl(43,56%,61%)] to-[hsl(43,56%,51%)] hover:from-[hsl(43,56%,51%)] hover:to-[hsl(43,56%,41%)] text-[hsl(210,50%,10%)] font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <motion.div
                        className="w-4 h-4 border-2 border-current border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                      />
                      Entrando...
                    </span>
                  ) : (
                    "Entrar"
                  )}
                </Button>
              </motion.div>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
